import type { FieldType } from '@/features/admin/forms/types'

/**
 * Format-level validators for specialized TEXT variants. Each returns null when valid
 * or a Polish error message. Empty/blank values are accepted - "required" is enforced
 * separately by the form (FieldDefinition.isRequired).
 */
export type FormatValidator = (value: string) => string | null

const validators: Partial<Record<FieldType, FormatValidator>> = {
  EMAIL: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Niepoprawny adres e-mail.'),
  // Phone: PL friendly - digits, spaces, dashes, optional + prefix; 9-15 digit-equivalent.
  PHONE: (v) => {
    const digits = v.replace(/[\s-]/g, '')
    if (!/^\+?[0-9]{9,15}$/.test(digits)) return 'Niepoprawny numer telefonu.'
    return null
  },
  URL: (v) => {
    try {
      const u = new URL(v.includes('://') ? v : `https://${v}`)
      return u.hostname.includes('.') ? null : 'Niepoprawny URL.'
    } catch {
      return 'Niepoprawny URL.'
    }
  },
  // IBAN: PL or international - basic length check (15-34 alphanumeric, starts with 2 letters).
  IBAN: (v) => {
    const trimmed = v.replace(/\s/g, '').toUpperCase()
    return /^[A-Z]{2}[0-9A-Z]{13,32}$/.test(trimmed) ? null : 'Niepoprawny IBAN.'
  },
  // NIP: PL - 10 digits, optional dashes ignored.
  NIP: (v) => {
    const digits = v.replace(/[-\s]/g, '')
    return /^[0-9]{10}$/.test(digits) ? null : 'NIP musi mieć 10 cyfr.'
  },
  // PESEL: PL - 11 digits.
  PESEL: (v) => (/^[0-9]{11}$/.test(v.trim()) ? null : 'PESEL musi mieć 11 cyfr.'),
}

/** Returns format validation error for {@link fieldType}, or null when value is valid/empty. */
export function validateFieldFormat(fieldType: FieldType, value: unknown): string | null {
  const validator = validators[fieldType]
  if (!validator) return null
  if (value === null || value === undefined || value === '') return null
  if (typeof value !== 'string') return null
  return validator(value)
}
